import React, { useRef, useState } from "react";
import { UploadCloud, X, Image as ImageIcon, Loader2 } from "lucide-react";
import api, { resolveImg } from "../lib/api";
import { toast } from "sonner";

/**
 * Drag-and-drop image uploader with live preview.
 * Props:
 *   - value: current image URL (from parent state)
 *   - onChange: called with new URL after successful upload
 *   - label: optional label
 *   - testid: base test id
 */
export default function ImageUpload({ value, onChange, label = "Image", testid = "image-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type)) {
      toast.error("Please upload a JPG, PNG, WEBP, or GIF image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`Image is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 5 MB.`);
      return;
    }

    setUploading(true);
    const form = new FormData();
    form.append("file", file);

    try {
      const { data } = await api.post("/admin/upload/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const clear = () => onChange("");

  return (
    <div data-testid={testid}>
      <span className="text-xs font-medium uppercase tracking-wide text-[var(--ink-forest)] block mb-1.5">
        {label}
      </span>

      {value ? (
        <div className="relative rounded-xl overflow-hidden border border-[var(--border)] bg-[var(--muted)]" data-testid={`${testid}-preview`}>
          <img src={resolveImg(value)} alt="" className="w-full h-48 object-cover" />
          <div className="absolute top-2 right-2 flex gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="bg-[var(--bg-surface)] text-[var(--ink-forest)] px-3 py-1.5 rounded-full text-xs font-medium hover:bg-white shadow-md disabled:opacity-60"
              data-testid={`${testid}-replace`}
            >
              {uploading ? "Uploading…" : "Replace"}
            </button>
            <button
              type="button"
              onClick={clear}
              className="bg-[var(--bg-surface)] text-rose-700 w-8 h-8 rounded-full text-sm hover:bg-white shadow-md flex items-center justify-center"
              aria-label="Remove image"
              data-testid={`${testid}-remove`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          disabled={uploading}
          className={`w-full rounded-xl border-2 border-dashed p-6 flex flex-col items-center justify-center text-center transition-colors ${
            dragOver
              ? "border-[var(--terracotta)] bg-[var(--terracotta)]/5"
              : "border-[var(--border)] bg-[var(--bg-cream)] hover:border-[var(--terracotta)]"
          }`}
          data-testid={`${testid}-dropzone`}
        >
          {uploading ? (
            <>
              <Loader2 className="w-8 h-8 text-[var(--terracotta)] mb-2 animate-spin" />
              <p className="text-sm text-[var(--muted-fg)]">Uploading…</p>
            </>
          ) : (
            <>
              <div className="w-11 h-11 rounded-full bg-[var(--terracotta)]/10 flex items-center justify-center mb-3">
                <UploadCloud className="w-5 h-5 text-[var(--terracotta)]" />
              </div>
              <p className="text-sm font-medium text-[var(--ink-forest)]">
                Drop an image here, or <span className="text-[var(--terracotta)] underline">click to browse</span>
              </p>
              <p className="text-xs text-[var(--muted-fg)] mt-1.5">JPG, PNG, WEBP · up to 5 MB</p>
            </>
          )}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testid}-input`}
      />

      {value && (
        <p className="mt-2 text-[10px] text-[var(--muted-fg)] break-all flex items-center gap-1">
          <ImageIcon className="w-3 h-3" />
          <span className="truncate">{value}</span>
        </p>
      )}
    </div>
  );
}
